import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Footprints, Plus, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const isoWeek = (d) => { const dt = new Date(d + 'T00:00:00'); const day = dt.getUTCDay() || 7; dt.setUTCDate(dt.getUTCDate() + 4 - day); const ys = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1)); return `${dt.getUTCFullYear()}-W${String(Math.ceil((((dt - ys) / 86400000) + 1) / 7)).padStart(2, '0')}`; };
const weekRange = (d) => { const dt = new Date(d + 'T00:00:00'); const day = (dt.getUTCDay() + 6) % 7; const mon = new Date(dt); mon.setUTCDate(dt.getUTCDate() - day); const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6); const fmt = (x) => `${String(x.getUTCDate()).padStart(2, '0')} ${x.toLocaleString('pt-PT', { month: 'short', timeZone: 'UTC' }).replace('.', '')}`; return `${fmt(mon)}–${fmt(sun)}`; };

export function computeStepsWeeklyAverages(records) {
  const buckets = {};
  (records || []).forEach(r => { const k = isoWeek(r.date); if (!buckets[k]) buckets[k] = { week: k, label: weekRange(r.date), values: [] }; buckets[k].values.push(parseInt(r.steps, 10) || 0); });
  return Object.values(buckets).filter(b => b.values.length >= 1)
    .sort((a, b) => a.week.localeCompare(b.week))
    .map(b => ({ ...b, avg: Math.round(b.values.reduce((a, c) => a + c, 0) / b.values.length) }));
}

export default function PassosPage() {
  const confirmDelete = useConfirmDelete();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], steps: '' });

  const fetchRecords = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-steps`); setRecords(data || []); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleAdd = async () => {
    if (!form.steps) return;
    try {
      await axios.post(`${API}/api/my-steps`, { date: form.date, steps: parseInt(form.steps, 10) });
      toast.success('Passos guardados');
      setShowAdd(false); setForm({ date: new Date().toISOString().split('T')[0], steps: '' }); fetchRecords();
    } catch { toast.error('Erro ao guardar'); }
  };
  const del = async (id) => {
    const r = records.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de ${r?.date || ''} (${r?.steps || 0} passos).` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/my-steps/${id}`); toast.success('Registo eliminado'); fetchRecords(); } catch { /* noop */ }
  };

  const weekly = computeStepsWeeklyAverages(records);
  const chartData = records.slice(0, 14).reverse();
  const maxSteps = Math.max(...chartData.map(r => r.steps), 1);
  const latest = records[0];
  const currentWeekAvg = weekly.length ? weekly[weekly.length - 1] : null;

  return (
    <div className="fade-in" data-testid="passos-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="passos-title">Passos diários</h2>
          <p className="text-sm text-brand-muted mt-1">Regista os teus passos e acompanha a média semanal</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-steps-btn">
          <Plus size={16} /> Registar passos
        </button>
      </div>

      {(latest || currentWeekAvg) && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid="steps-summary">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-heading font-semibold text-brand-text">{latest ? latest.steps.toLocaleString('pt-PT') : '-'}</p>
              <p className="text-[10px] text-brand-muted">Último registo</p>
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold text-brand-green">{currentWeekAvg ? currentWeekAvg.avg.toLocaleString('pt-PT') : '-'}</p>
              <p className="text-[10px] text-brand-muted">Média semanal</p>
            </div>
          </div>
        </div>
      )}

      {chartData.length >= 2 && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5" data-testid="steps-chart">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-4">Evolução (últimos registos)</h3>
          <div className="flex items-end justify-between gap-1 h-36">
            {chartData.map((r) => (
              <div key={r.id} className="flex-1 flex flex-col items-center justify-end h-full" title={`${r.date}: ${r.steps} passos`}>
                <div className="w-full rounded-t bg-brand-green" style={{ height: `${Math.max((r.steps / maxSteps) * 100, 2)}%` }} />
                <span className="text-[8px] text-brand-muted mt-1">{r.date?.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {weekly.length > 0 && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-2xl p-3 mb-4" data-testid="steps-weekly-averages">
          <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Médias semanais</h4>
          <div className="space-y-1">
            {weekly.slice().reverse().map((b, idx) => {
              const prev = weekly.slice().reverse()[idx + 1];
              const delta = prev ? b.avg - prev.avg : null;
              return (
                <div key={b.week} className="flex items-center justify-between text-xs flex-wrap" data-testid={`steps-weekly-avg-${b.week}`}>
                  <span className="text-brand-text">Semana {b.label}: <strong>{b.avg.toLocaleString('pt-PT')} passos</strong> <span className="text-brand-muted">({b.values.length} dia{b.values.length > 1 ? 's' : ''})</span></span>
                  {delta != null && delta !== 0 && (
                    <span className={`text-[10px] font-medium ${delta > 0 ? 'text-brand-green' : 'text-red-500'}`}>{delta > 0 ? '+' : ''}{delta.toLocaleString('pt-PT')} face à anterior</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-8 text-brand-muted">A carregar...</div> : records.length === 0 ? (
        <div className="text-center py-16">
          <Footprints size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">Sem registos de passos.</p>
          <p className="text-xs text-brand-placeholder mt-1">Regista os teus passos para acompanhar a evolução.</p>
        </div>
      ) : (
        <div>
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Histórico</h3>
          <div className="space-y-2">
            {records.map(r => (
              <div key={r.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between" data-testid={`steps-record-${r.id}`}>
                <span className="text-xs text-brand-muted">{fmtDatePt(r.date)}</span>
                <div className="flex items-center gap-3">
                  <p className="text-sm font-heading font-semibold text-brand-text">{(r.steps || 0).toLocaleString('pt-PT')} passos</p>
                  <button onClick={() => del(r.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-steps-${r.id}`}><Trash size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar passos</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="steps-date" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Passos</Label><Input type="number" step="1" value={form.steps} onChange={e => setForm({ ...form, steps: e.target.value })} placeholder="Ex: 8500" className="rounded-xl border-brand-border" data-testid="steps-value" /></div>
            <button onClick={handleAdd} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-steps-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
